import { processRawValue } from '@atlaskit/editor-common/process-raw-value';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { Transform } from '@atlaskit/editor-prosemirror/transform';

import type { Suggestion, ToolCall } from '../../../aiSuggestionsPluginType';

import { wrapNode } from './wrapNode';

function createNodeFromToolCallValue(schema: Schema, value: string): PMNode | undefined {
	try {
		const parsed = typeof value === 'string' ? JSON.parse(value) : value;
		return processRawValue(schema, parsed);
	} catch {
		return undefined;
	}
}

function getPositionsByLocalId(
	doc: PMNode,
	localId: string,
): { from: number; to: number } | undefined {
	let result: { from: number; to: number } | undefined;
	doc.descendants((node, pos) => {
		if (result) {
			return false;
		}
		if (node.attrs.localId === localId) {
			result = { from: pos, to: pos + node.nodeSize };
		}
		return !result;
	});
	return result;
}

// Looks up `localId` in the original snapshot and maps the position into current `tr.doc`
// coords. Resolving against the live `tr.doc` would match a duplicate localId inserted by an
// earlier tool call (e.g. a "move" encoded as insertNodeBefore + removeNode). Returns undefined
// if the node was deleted by an earlier step.
function resolveMappedPositions(
	tr: Transaction | Transform,
	originalDoc: PMNode,
	baseStepCount: number,
	localId: string,
): { from: number; to: number } | undefined {
	const original = getPositionsByLocalId(originalDoc, localId);
	if (!original) {
		return undefined;
	}
	const mapping = tr.mapping.slice(baseStepCount);
	const from = mapping.map(original.from, 1);
	const to = mapping.map(original.to, -1);
	if (to <= from) {
		return undefined;
	}
	return { from, to };
}

function applyToolCall(
	tr: Transaction | Transform,
	toolCall: ToolCall,
	originalDoc: PMNode,
	baseStepCount: number,
): void {
	if (toolCall.name === 'removeNodes') {
		for (const id of toolCall.arguments.localIds) {
			const positions = resolveMappedPositions(tr, originalDoc, baseStepCount, id);
			if (positions) {
				tr.delete(positions.from, positions.to);
			}
		}
		return;
	}

	const positions = resolveMappedPositions(
		tr,
		originalDoc,
		baseStepCount,
		toolCall.arguments.localId,
	);

	if (!positions) {
		return;
	}

	switch (toolCall.name) {
		case 'replaceNode':
		case 'insertNodeBefore':
		case 'insertNodeAfter': {
			const node = createNodeFromToolCallValue(tr.doc.type.schema, toolCall.arguments.value);
			if (!node) {
				return;
			}
			// An attribute-only replaceNode (e.g. promoting a heading from H4 to H3) keeps the
			// node's type and content identical. Encoding it as a full replace yields a ReplaceStep
			// whose granular diff is empty (the text is unchanged), so the change never surfaces.
			// Apply it via setNodeMarkup instead so the diff plugin renders it as a node change.
			const existingNode = tr.doc.nodeAt(positions.from);
			if (
				toolCall.name === 'replaceNode' &&
				existingNode &&
				existingNode.type === node.type &&
				existingNode.content.eq(node.content)
			) {
				tr.setNodeMarkup(positions.from, undefined, node.attrs, node.marks);
				return;
			}
			const from = toolCall.name === 'insertNodeAfter' ? positions.to : positions.from;
			const to = toolCall.name === 'replaceNode' ? positions.to : from;
			tr.replaceWith(from, to, node);
			return;
		}
		case 'removeNode':
			tr.delete(positions.from, positions.to);
			return;
		case 'wrap':
		case 'wrapNode':
			wrapNode({
				toolCallArguments: toolCall.arguments,
				tr,
				pos: positions.from,
			});
			return;
		case 'setAttrs': {
			const node = tr.doc.nodeAt(positions.from);
			if (!node) {
				return;
			}
			tr.setNodeMarkup(positions.from, undefined, {
				...node.attrs,
				...toolCall.arguments.attrs,
			});
			return;
		}
	}
}

export function applyToolCalls<T extends Transaction | Transform>({
	tr,
	suggestion,
}: {
	suggestion: Suggestion;
	tr: T;
}): T {
	const originalDoc = tr.doc;
	const baseStepCount = tr.steps.length;
	for (const toolCall of suggestion.toolCalls ?? []) {
		applyToolCall(tr, toolCall, originalDoc, baseStepCount);
	}
	return tr;
}
